import {useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {gothenburgApiAuthHeaders} from 'appUtils/authentication/authenticationUtils.ts';

// Helper function to format the date for the query in ISO 8601 format (YYYY-MM-DDTHH:mm:ss)
const formatDate = (date: Date): string => {
  return date.toISOString(); // Format as YYYY-MM-DDTHH:mm:ss
};

// Function to fetch warning data from the new API with query parameters
const fetchWarningsData = async (
  fordons_id?: string[],
  start_date: string = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Default: last 10 minutes
  end_date: string = formatDate(new Date()), // Default: current date
  warning_type: string = 'warning', // Default: 'warning'
  missing_tag_id?: string,
): Promise<any[]> => {
  const softprioEndpointBase = process.env.REACT_APP_DETECTOR_BASE_API_URL;
  const warningApiUrl = new URL(`${softprioEndpointBase}/warnings`);
  const params: Record<string, string> = {
    warning_type,
    start_date,
    end_date,
  };

  // Add optional query parameters if provided
  if (fordons_id && fordons_id.length > 0) {
    params.fordons_id = fordons_id.join(',');
  }

  if (missing_tag_id) {
    params.missing_tag_id = missing_tag_id; // Pass missing_tag_id as string (if not integer)
  }

  // Append query parameters to the URL
  Object.keys(params).forEach((key) => {
    warningApiUrl.searchParams.append(key, params[key]);
  });

  const response = await fetch(warningApiUrl.toString(), {
    method: 'GET',
    headers: {accept: 'application/json', ...gothenburgApiAuthHeaders()},
  });

  if (!response.ok) {
    throw new Error('Failed to fetch warning data');
  }

  const result = await response.json();
  // Ensure the result is an array
  if (Array.isArray(result)) {
    return result;
  } else {
    throw new Error('Unexpected API response structure');
  }
};

// Custom hook to fetch warning data using React Query
const useFetchWarnings = (
  fordons_id?: string[],
  start_date?: string,
  end_date?: string,
  warning_type: string = 'warning',
  missing_tag_id?: string,
) => {
  const [errors, setErrors] = useState<string[]>([]);

  // Fetch warning data
  const {
    data: warningsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'fetchWarnings',
      fordons_id,
      start_date,
      end_date,
      warning_type,
      missing_tag_id,
    ],
    queryFn: () => fetchWarningsData(),
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    enabled: true,
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      setErrors((prevErrors) => [...prevErrors, (error as Error).message]);
    }
  }, [error]);

  return {
    data: warningsData,
    isLoading,
    errors,
  };
};

export default useFetchWarnings;
